import { useMutation, useQuery } from '@apollo/client/react';
import { type PropsWithChildren, useState } from 'react';
import { toast } from 'sonner';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/Dialog';
import { EditButton } from '@/components/EditButton';
import FileInput from '@/components/FileInput';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Spinner } from '@/components/ui/spinner';
import { fileToBase64 } from '@/lib/utils';

import { GET_CASE_SUMMARY, UPSERT_CASE_SUMMARY } from './graphql';
import type { GetCaseSummaryResponse } from './types';

const SectionHeader = ({
  children,
  title
}: PropsWithChildren<{ title: string }>) => (
  <div className="mb-2 flex items-center justify-between gap-3">
    <h2 className="text-lg font-semibold">{title}</h2>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);

const Card = ({ children }: PropsWithChildren) => (
  <div className="bg-card rounded-md border p-5">{children}</div>
);

const CaseSummaryTab = ({ dealId }: { dealId: string }) => {
  const [isOpen, setIsOpen] = useState(false);

  const { data, loading } = useQuery<
    GetCaseSummaryResponse,
    { dealOpportunityId: string }
  >(GET_CASE_SUMMARY, {
    variables: { dealOpportunityId: dealId }
  });

  const [upsertCaseSummary, { loading: isUpdating }] = useMutation(
    UPSERT_CASE_SUMMARY,
    { refetchQueries: [GET_CASE_SUMMARY] }
  );

  if (loading) {
    return (
      <div className="grid h-64 place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  const caseSummary = data?.caseSummaryByDeal;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      const formData = new FormData(event.currentTarget);
      const imageFile = formData.get('cases-image-field');
      let casesImage: null | string = null;

      if (imageFile instanceof File && imageFile.size > 0) {
        casesImage = await fileToBase64(imageFile);
      }

      const { errors } = await upsertCaseSummary({
        variables: {
          dealOpportunityId: dealId,
          values: {
            ...(casesImage !== null ? { casesImage } : {})
          }
        }
      });

      if (errors?.length) {
        throw new Error(errors[0].message);
      }

      setIsOpen(false);
    } catch (error_) {
      const error = error_ as Error;
      console.error(error);
      toast.error(error.message);
    }
  };

  return (
    <div className="space-y-3 pt-5">
      <SectionHeader title="Case Summary">
        <Dialog onOpenChange={setIsOpen} open={isOpen}>
          <DialogTrigger asChild>
            <EditButton />
          </DialogTrigger>
          <DialogContent aria-describedby={undefined}>
            <DialogHeader>
              <DialogTitle>
                Edit Case Summary Image
                <Badge variant="yellow">Unsaved</Badge>
              </DialogTitle>
            </DialogHeader>
            <DialogBody>
              <form
                id="case-summary-form"
                noValidate
                onSubmit={event => void handleSubmit(event)}
              >
                <FieldGroup>
                  <Field>
                    <FieldLabel htmlFor="cases-image-field">Photo</FieldLabel>
                    <FileInput
                      accept="image/*"
                      data={caseSummary?.casesImage ?? null}
                      id="cases-image-field"
                      name="cases-image-field"
                    />
                  </Field>
                </FieldGroup>
              </form>
            </DialogBody>
            <DialogFooter>
              <Button
                disabled={isUpdating}
                form="case-summary-form"
                type="submit"
              >
                {isUpdating && <Spinner />}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SectionHeader>
      <Card>
        <div className="space-y-2">
          <div className="text-sm font-medium">Case Summary Image</div>
          <FileInput
            accept="image/*"
            data={caseSummary?.casesImage ?? null}
            readOnly
          />
        </div>
      </Card>
    </div>
  );
};

export default CaseSummaryTab;
